// 手机输入和消息工具分行呈现，保留清晰触控面积与可滚动详情；桌面布局及业务操作沿用原实现。
import React, { useEffect, useRef, useState } from "react";
import { Play, Pause, Loader2, FileText, RotateCcw, ChevronDown, ChevronUp, Pencil, Sparkles } from "lucide-react";
import { fileUrl, fmtDur } from "../../lib/chat";

/* 转文字编辑（发送人 / 经理，随时可改）：保存 → PUT /chat/messages/{id}/transcript；显示「已修正」并可悬停看原始识别 */
const TranscriptEditor = ({ m, zh, mine, initial, onSave, onCancel }) => {
  const [txt, setTxt] = useState(initial || "");
  const [busy, setBusy] = useState(false);
  const save = async () => { if (!txt.trim()) return; setBusy(true); const ok = await onSave(txt.trim()); setBusy(false); if (ok) onCancel(); };
  return (
    <div className="mt-1.5 min-w-0 w-full sm:min-w-[220px]" data-testid={`chat-voice-tr-editor-${m.id}`}>
      <textarea data-testid={`chat-voice-tr-input-${m.id}`} value={txt} onChange={(e) => setTxt(e.target.value)} rows={3} autoFocus placeholder={zh ? "输入这段语音的文字…" : "Type what was said…"}
        onKeyDown={(e) => { if (e.key === "Escape") onCancel(); if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) save(); }}
        className={`w-full text-[12px] rounded-lg border px-2 py-1 outline-none ${mine ? "bg-white/15 border-white/30 text-white placeholder:text-white/60" : "bg-white border-slate-200 text-slate-800"}`} />
      <div className="flex items-center justify-end gap-1.5 mt-1 text-[11px]">
        <button type="button" data-testid={`chat-voice-tr-cancel-${m.id}`} onClick={onCancel} className={mine ? "text-white/80" : "text-slate-500"}>{zh ? "取消" : "Cancel"}</button>
        <button type="button" data-testid={`chat-voice-tr-save-${m.id}`} disabled={busy || !txt.trim()} onClick={save} className={`rounded-full px-2.5 py-0.5 font-semibold disabled:opacity-50 inline-flex items-center gap-1 ${mine ? "bg-white text-[#4F6BFF]" : "bg-[#4F6BFF] text-white"}`}>{busy && <Loader2 className="h-3 w-3 animate-spin" />}{zh ? "保存修正" : "Save"}</button>
      </div>
    </div>
  );
};

/* 语音气泡：播放 / 暂停 · 进度（可点选位置）· 时长 · 转文字（折叠；转写中 / 失败可重试 · 可修正）。音频来自对象存储 /files/{id}/download */
export const VoiceBubble = ({ m, zh, mine, onRetranscribe, canEditTranscript, onEditTranscript }) => {
  const att = (m.attachments || [])[0];
  const v = m.voice || {};
  const audio = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(Number(v.duration) || 0);
  const [loading, setLoading] = useState(false);
  const [showTr, setShowTr] = useState(false);
  const [editing, setEditing] = useState(false);
  const [err, setErr] = useState(false);
  useEffect(() => () => { try { audio.current?.pause(); } catch { /* */ } }, []);
  useEffect(() => { setEditing(false); }, [v.transcript, v.transcriptEditedAt]);
  useEffect(() => {   // 右键菜单「修正转写」
    const h = (e) => { if (e.detail?.id === m.id && canEditTranscript) { setShowTr(true); setEditing(true); } };
    window.addEventListener("chat:edit-transcript", h);
    return () => window.removeEventListener("chat:edit-transcript", h);
  }, [m.id, canEditTranscript]);
  if (!att) return <p className="text-[12px] text-slate-400">{zh ? "[语音文件缺失]" : "[voice file missing]"}</p>;
  const total = dur || Number(v.duration) || 0;
  const pct = total > 0 ? Math.min(100, (cur / total) * 100) : 0;
  const toggle = async () => {
    const a = audio.current;
    if (!a) return;
    if (playing) { a.pause(); return; }
    try { setLoading(true); await a.play(); } catch { setErr(true); } finally { setLoading(false); }
  };
  const seek = (e) => {
    const a = audio.current;
    if (!a || !total) return;
    const r = e.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width));
    try { a.currentTime = ratio * total; setCur(ratio * total); } catch { /* */ }
  };
  const width = Math.min(260, 140 + Math.round(total) * 3);
  const fg = mine ? "text-white" : "text-slate-800";
  const sub = mine ? "text-white/80" : "text-slate-500";
  const bar = mine ? "bg-white/25" : "bg-slate-200";
  const fill = mine ? "bg-white" : "bg-[#4F6BFF]";
  const tr = v.transcript;
  const status = v.status || (tr ? "done" : "pending");
  const edited = !!v.transcriptEditedAt;
  const startEdit = () => { setShowTr(true); setEditing(true); };
  const editBtn = canEditTranscript && !editing && status !== "pending" ? (
    <button type="button" data-testid={`chat-voice-tr-edit-${m.id}`} onClick={startEdit} title={zh ? "修正转写文字" : "Edit transcript"} className={`inline-flex items-center gap-0.5 ${fg} hover:underline underline-offset-2`}><Pencil className="h-2.5 w-2.5" />{tr ? (zh ? "修正" : "Edit") : (zh ? "手动输入" : "Type it")}</button>
  ) : null;
  return (
    <div data-testid={`chat-voice-${m.id}`} data-status={status} data-playing={playing ? 1 : 0} data-edited={edited ? 1 : 0} style={{ minWidth: width }} className="select-none">
      <audio ref={audio} src={fileUrl(att.fileId)} preload="metadata"
        onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => { setPlaying(false); setCur(0); }}
        onTimeUpdate={(e) => setCur(e.currentTarget.currentTime || 0)}
        onLoadedMetadata={(e) => { const d = e.currentTarget.duration; if (Number.isFinite(d) && d > 0 && !v.duration) setDur(d); }}
        onError={() => setErr(true)} />
      <div className="flex items-center gap-2">
        <button type="button" data-testid={`chat-voice-play-${m.id}`} onClick={toggle} disabled={err} aria-label={playing ? "pause" : "play"}
          className={`h-11 w-11 sm:h-8 sm:w-8 shrink-0 rounded-full flex items-center justify-center ${mine ? "bg-white/20 hover:bg-white/30 text-white" : "bg-[#4F6BFF] text-white hover:bg-[#3f5af0]"} disabled:opacity-40`}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 ml-0.5" />}
        </button>
        <div className="flex-1 min-w-[90px]">
          <div role="progressbar" aria-valuenow={Math.round(pct)} data-testid={`chat-voice-progress-${m.id}`} onClick={seek} className={`h-1.5 w-full rounded-full ${bar} cursor-pointer relative overflow-hidden`}>
            <div className={`absolute inset-y-0 left-0 rounded-full ${fill} transition-[width] duration-150`} style={{ width: `${pct}%` }} />
          </div>
          <div className={`mt-1 flex items-center justify-between gap-2 text-[10px] ${sub}`}>
            <span data-testid={`chat-voice-dur-${m.id}`}>{playing || cur > 0 ? `${fmtDur(cur)} / ${fmtDur(total)}` : `${fmtDur(total)}`}</span>
            <span className="inline-flex items-center gap-2">
              {err ? <span className="text-rose-300">{zh ? "无法播放" : "Can't play"}</span>
                : status === "pending" ? <span className="inline-flex items-center gap-0.5"><Loader2 className="h-2.5 w-2.5 animate-spin" />{zh ? "转写中…" : "Transcribing…"}</span>
                  : status === "failed" || status === "empty" ? (
                    <button type="button" data-testid={`chat-voice-retry-${m.id}`} onClick={() => onRetranscribe?.(m)} className={`inline-flex items-center gap-0.5 underline-offset-2 hover:underline ${fg}`}><RotateCcw className="h-2.5 w-2.5" />{status === "empty" ? (zh ? "未识别到语音 · 重试" : "Nothing recognised · retry") : (zh ? "转写失败 · 重试" : "Failed · retry")}</button>
                  ) : tr ? (
                    <button type="button" data-testid={`chat-voice-tr-toggle-${m.id}`} data-open={showTr ? 1 : 0} onClick={() => setShowTr((x) => !x)} className={`inline-flex items-center gap-0.5 ${fg} hover:underline underline-offset-2`}><FileText className="h-2.5 w-2.5" />{zh ? "转文字" : "Transcript"}{showTr ? <ChevronUp className="h-2.5 w-2.5" /> : <ChevronDown className="h-2.5 w-2.5" />}</button>
                  ) : null}
              {editBtn}
            </span>
          </div>
        </div>
      </div>
      {editing ? <TranscriptEditor m={m} zh={zh} mine={mine} initial={tr || ""} onSave={(t) => onEditTranscript?.(m, t)} onCancel={() => setEditing(false)} />
        : (
          <>
            {v.summaryStatus === "pending" && !showTr && <p className={`mt-1 text-[10px] ${sub} inline-flex items-center gap-1`} data-testid={`chat-voice-summary-pending-${m.id}`}><Loader2 className="h-2.5 w-2.5 animate-spin" />{zh ? "摘要生成中…" : "Summarising…"}</p>}
            {v.summary && (
              <div className={`mt-1.5 pt-1.5 border-t ${mine ? "border-white/20" : "border-slate-200"}`} data-testid={`chat-voice-summary-${m.id}`}>
                <p className={`text-[12px] leading-snug ${mine ? "text-white" : "text-slate-800"}`}><Sparkles className="inline h-3 w-3 mr-1 -mt-0.5 opacity-70" />{v.summary}</p>
                {!showTr && tr && <button type="button" data-testid={`chat-voice-full-${m.id}`} onClick={() => setShowTr(true)} className={`mt-0.5 text-[10px] ${fg} underline underline-offset-2 opacity-80 hover:opacity-100`}>{zh ? "展开全文" : "Show full text"}</button>}
              </div>
            )}
            {showTr && tr && (
              <div className={`mt-1.5 pt-1.5 border-t ${mine ? "border-white/20" : "border-slate-200"}`}>
                <p data-testid={`chat-voice-transcript-${m.id}`} className={`${mine ? "text-white/95" : "text-slate-700"} text-[12px] leading-relaxed whitespace-pre-wrap break-words`}>{tr}</p>
                {edited && <span data-testid={`chat-voice-tr-edited-${m.id}`} title={v.transcriptRaw ? (zh ? `原始识别：${v.transcriptRaw}` : `Original: ${v.transcriptRaw}`) : undefined} className={`mt-0.5 inline-flex items-center gap-0.5 text-[9px] ${sub}`}><Pencil className="h-2.5 w-2.5" />{zh ? "已修正" : "edited"}{v.transcriptEditedByName ? ` · ${v.transcriptEditedByName}` : ""}</span>}
              </div>
            )}
          </>
        )}
    </div>
  );
};
